import { FeatureFlagValueType as VT, type FeatureFlagValueType } from "./types";

export type ParseResult =
  | { readonly ok: true; readonly value: unknown }
  | { readonly ok: false; readonly errorKey: string };

/**
 * Parse a raw text input into the JSON value the API expects for a flag
 * of the given `valueType`. The UI narrows the input widget per type, so
 * the parsing here is mostly about validating the shape before sending.
 *
 * - BOOLEAN: expects literal `"true"` / `"false"` (the form uses a
 *   toggle, so this path is defensive).
 * - NUMBER: parsed with `Number()`; NaN / Infinity are rejected.
 * - STRING: the raw text is the value. Empty strings are allowed — the
 *   API treats them as intentional empty values.
 * - JSON: must parse as valid JSON. `null` is explicitly allowed per
 *   the API contract ("JSON-type flags accept null").
 */
export function parseInputValue(raw: string, valueType: FeatureFlagValueType): ParseResult {
  switch (valueType) {
    case VT.BOOLEAN: {
      if (raw === "true") return { ok: true, value: true };
      if (raw === "false") return { ok: true, value: false };
      return { ok: false, errorKey: "featureFlags.edit.errors.booleanRequired" };
    }
    case VT.NUMBER: {
      const trimmed = raw.trim();
      if (trimmed.length === 0) {
        return { ok: false, errorKey: "featureFlags.edit.errors.numberRequired" };
      }
      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed)) {
        return { ok: false, errorKey: "featureFlags.edit.errors.numberRequired" };
      }
      return { ok: true, value: parsed };
    }
    case VT.STRING:
      return { ok: true, value: raw };
    case VT.JSON: {
      const trimmed = raw.trim();
      if (trimmed.length === 0) {
        return { ok: false, errorKey: "featureFlags.edit.errors.jsonRequired" };
      }
      try {
        return { ok: true, value: JSON.parse(trimmed) };
      } catch {
        return { ok: false, errorKey: "featureFlags.edit.errors.jsonInvalid" };
      }
    }
    default:
      return { ok: false, errorKey: "featureFlags.edit.errors.jsonInvalid" };
  }
}

/**
 * Default text representation for the edit input, derived from the
 * current runtime value. JSON types get pretty-printed for editability;
 * everything else is rendered tightly.
 */
export function valueToInputString(value: unknown, valueType: FeatureFlagValueType): string {
  if (value === null || value === undefined) {
    return valueType === VT.STRING ? "" : "null";
  }
  switch (valueType) {
    case VT.BOOLEAN:
      return value === true ? "true" : "false";
    case VT.NUMBER:
      return typeof value === "number" ? String(value) : JSON.stringify(value);
    case VT.STRING:
      return typeof value === "string" ? value : JSON.stringify(value);
    case VT.JSON:
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    default:
      return JSON.stringify(value);
  }
}

/**
 * Deep structural equality via JSON. Sufficient for flag values because
 * the API contract only serializes to JSON — any comparison a non-JSON
 * runtime check would catch wouldn't survive the round-trip anyway.
 */
export function valuesEqual(a: unknown, b: unknown): boolean {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return a === b;
  }
}
